import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Inbox, RefreshCw } from "lucide-react";

import { PageHeader } from "@/components/app-shell";
import { NotificationGroupList } from "@/components/notification-list";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNotifications, useRefreshNotifications } from "@/hooks/use-notifications";
import { NOTIFICATION_NEWS_DAYS } from "@somasi/shared";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/notifications")({
  head: () => ({
    meta: [
      { title: "Notifications — Somasi Ops" },
      {
        name: "description",
        content: "Everything waiting on your decision, and what was decided about your requests.",
      },
    ],
  }),
  component: NotificationsPage,
});

/**
 * Everything the bell shows, with nothing cut.
 *
 * The same feed, the same two headings, the same rows — this page exists
 * because the popover is a glance and a queue is work. It is also where the
 * sidebar entry goes: a bell lives in a top bar, but "the list of things
 * waiting on me" is a place in the app, and a place needs a URL.
 *
 * There is no "mark all read" and nothing to dismiss. A notification here is a
 * request somebody is waiting on, and the only way to be rid of one is to
 * decide it — which is what the row links to. See
 * `packages/shared/src/notifications.ts`.
 */
function NotificationsPage() {
  const { waiting, news, feed, seenAt, isPending, isError, error, markSeen } = useNotifications();
  const refresh = useRefreshNotifications();

  /*
   * Opening the page is the look. Unlike the bell this needs an effect rather
   * than an open handler — arriving *is* the open — and it runs once because
   * `markSeen` is stable and a second mark on the same visit would only move
   * the line past news that arrived while the page sat there.
   */
  useEffect(() => markSeen(), [markSeen]);

  const empty = waiting.length === 0 && news.length === 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications"
        description="What is waiting on your decision, and what was decided about your own requests."
        actions={
          <Button variant="outline" onClick={refresh} disabled={isPending}>
            <RefreshCw className={cn("mr-2 h-4 w-4", isPending && "animate-spin")} />
            Refresh
          </Button>
        }
      />

      {isError && (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            {error?.message ?? "Notifications could not be loaded."}
          </CardContent>
        </Card>
      )}

      {!isError && (
        <Card>
          <CardContent className="p-2">
            {isPending && (
              <p className="px-3 py-12 text-center text-sm text-muted-foreground">Looking…</p>
            )}

            {!isPending && empty && (
              <div className="flex flex-col items-center gap-2 px-3 py-12 text-center">
                <Inbox className="h-7 w-7 text-muted-foreground" />
                <p className="text-sm font-medium">Nothing is waiting on you.</p>
                <p className="text-sm text-muted-foreground">
                  Leave, work-from-home and time corrections appear here while they are undecided.
                </p>
              </div>
            )}

            <NotificationGroupList label="Waiting on you" notifications={waiting} seenAt={seenAt} />
            <NotificationGroupList label="Your requests" notifications={news} seenAt={seenAt} />
          </CardContent>
        </Card>
      )}

      {!isPending && !isError && !empty && (
        <p className="text-xs text-muted-foreground">
          {feed.truncated
            ? "Only the first few are listed — open the page a notification points at to see the rest. "
            : ""}
          A decision stays here for {NOTIFICATION_NEWS_DAYS} days. Everything else leaves the list
          when it is decided.
        </p>
      )}
    </div>
  );
}
